import {useEffect, useState} from 'react';

import {InterfaceEventsEnum} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsScalarMetricsEnum} from '../../../models/api/analyticsMetricsModel';
import {getAnalyticsSelectedMetrics} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {ReportElementIdsEnum} from '../reportHelpers/reportTypes';
import {useAnalyticsTracking} from '../tracking/useAnalyticsTracking';
import {useAnalyticsUpdateSelectedMetrics} from './useAnalyticsUpdateSelectedMetrics';

/**
 * Custom hook to handle a list of metrics and toggle their visibility status.
 * @deprecated Replaced by useAnalyticsSelectedMetrics for the v3.
 */
export const useAnalyticsHeroMetricPicker = (
  metrics: ReadonlyArray<AnalyticsScalarMetricsEnum>,
  reportElementId: ReportElementIdsEnum,
): [ReadonlyArray<AnalyticsScalarMetricsEnum>, (arg: AnalyticsScalarMetricsEnum) => void] => {
  // displayedMetrics will be the metrics exposed outside of the hook. They have an initial value but can be overridden by metrics set on the view.
  const [displayedMetrics, setDisplayedMetrics] =
    useState<ReadonlyArray<AnalyticsScalarMetricsEnum>>(metrics);

  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);

  const {updateSelectedMetrics, maybeFetchHiddenMetrics} = useAnalyticsUpdateSelectedMetrics();

  // This will trigger when we select a view or change the list of metrics.
  useEffect(() => {
    if (selectedMetrics[reportElementId]) {
      setDisplayedMetrics(selectedMetrics[reportElementId] as Array<AnalyticsScalarMetricsEnum>);
    } else {
      setDisplayedMetrics(metrics);
    } // Reset to original values.
  }, [selectedMetrics, reportElementId, metrics]);

  const trackAnalytics = useAnalyticsTracking();
  const trackChange = (metricType: AnalyticsScalarMetricsEnum, isAdded: boolean) =>
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_LIST_EDIT, {
      metric_type: metricType,
      selection_type: isAdded ? 'add' : 'remove',
    });

  const onMetricPickerEntryClick = (id: AnalyticsScalarMetricsEnum) => {
    const isCurrentlySelected = displayedMetrics.includes(id);
    trackChange(id, !isCurrentlySelected);
    let newDisplayedMetrics;
    if (isCurrentlySelected) {
      newDisplayedMetrics = displayedMetrics.filter((metric) => metric !== id);
    } else {
      // We selected a new metric. We may need to fetch it.
      maybeFetchHiddenMetrics(id);
      newDisplayedMetrics = [...displayedMetrics, id];
    }

    // When selecting new metrics in the dropdown, we update the store. This will update the UI in return through the useEffect.
    updateSelectedMetrics(newDisplayedMetrics, reportElementId);
  };

  return [displayedMetrics, onMetricPickerEntryClick];
};
